import { retrieveSchema, type RJSFSchema } from '@rjsf/utils';
import validator from '@rjsf/validator-ajv8';

import type { Organisation } from 'app/pages/PlusWebsites/hooks/useOrganisation';

/**
 * What the organisation record can contribute to a brand-new section, keyed by the field it fills.
 * The footer's contact block declares all four; the header declares none.
 */
const PREFILL_BY_FIELD: Record<string, (organisation: Organisation) => string | undefined> = {
  contactName: ({ primaryUser }) => usableText(' ', primaryUser?.firstName, primaryUser?.lastName),
  emailAddress: ({ primaryUser }) => usableText('', primaryUser?.email),
  phoneNumber: ({ primaryUser }) => usableText('', primaryUser?.telephone),
  contactAddress: ({ address }) =>
    usableText(', ', address?.street, address?.city, address?.postcode),
};

/**
 * Combines the parts that carry usable text. Contact records are often filled in to satisfy a
 * required field rather than to say anything — a contact person recorded as "." would otherwise
 * be preset as ". ", which is worse than leaving the field empty for the user to complete.
 */
function usableText(separator: string, ...parts: (string | undefined)[]): string | undefined {
  const usable = parts
    .map((part) => part?.trim())
    .filter((part): part is string => !!part && /[a-z0-9]/i.test(part));

  return usable.length > 0 ? usable.join(separator) : undefined;
}

/** Ignores case and separators, so `contact_name`, `contactName` and "Contact Name" all match. */
function normalise(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9]/g, '');
}

/**
 * Where a field sits in the schema, e.g. `['contactUs', 'contactName']` — the footer groups its
 * contact fields under a titled object, and that grouping is presentational, so nothing here may
 * depend on the fields being at the top level. A field is recognised by its declared name or, for
 * a schema that names it differently, by its label. Array items are skipped: a value inside a
 * repeated group has no single position to preset.
 *
 * @param resolved - a schema already expanded by `retrieveSchema`
 */
function findPath(
  resolved: RJSFSchema,
  rootSchema: RJSFSchema,
  field: string
): string[] | undefined {
  const target = normalise(field);
  let path: string[] | undefined;

  Object.entries(resolved.properties ?? {}).some(([name, declared]) => {
    if (typeof declared !== 'object') return false;

    const child = retrieveSchema(validator, declared, rootSchema);
    const labelled = typeof child.title === 'string' && normalise(child.title) === target;
    if (normalise(name) === target || labelled) {
      path = [name];
      return true;
    }

    const nested = findPath(child, rootSchema, field);
    if (nested) {
      path = [name, ...nested];
      return true;
    }

    return false;
  });

  return path;
}

function setAtPath(target: Record<string, unknown>, path: string[], value: string): void {
  const leaf = path[path.length - 1];
  if (!leaf) return;

  const container = path.slice(0, -1).reduce<Record<string, unknown>>((node, key) => {
    const next = (node[key] as Record<string, unknown> | undefined) ?? {};
    node[key] = next;
    return next;
  }, target);

  container[leaf] = value;
}

/**
 * The initial form data for a new section: every value this organisation already has on record,
 * placed where the schema declares the field. Returns undefined when the schema declares none of
 * them, leaving the form on its schema defaults — which rjsf merges in around whatever is preset.
 */
export function prefillFromOrganisation(
  schema: RJSFSchema,
  organisation: Organisation | null
): Record<string, unknown> | undefined {
  if (!organisation) return undefined;

  // rjsf expands `$ref`, `allOf` and friends before it renders, so anything reading the schema has
  // to resolve it the same way or it ends up disagreeing with the form the user is looking at.
  const resolved = retrieveSchema(validator, schema, schema);

  const prefill: Record<string, unknown> = {};
  Object.entries(PREFILL_BY_FIELD).forEach(([field, read]) => {
    const value = read(organisation);
    if (!value) return;

    const path = findPath(resolved, schema, field);
    if (path) setAtPath(prefill, path, value);
  });

  return Object.keys(prefill).length > 0 ? prefill : undefined;
}
